import { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { EXTERNAL_PATH } from 'lib/paths'
import {
  useAgreeUserPolicyAgreementPrivacyMutation,
  useAgreeUserPolicyAgreementTermMutation,
  useAgreeUserPolicyNewEmplMutation,
} from 'store/api/yay/user'

import Button from 'components/Button'
import { ExternalLink } from 'components/ExternalLink'

import './style.scss'

export const UserAgreementModal = ({
  returnPath,
  terms,
  privacy,
  empl,
}: {
  returnPath?: string
  terms?: string
  privacy?: string
  empl?: string
}) => {
  const { t } = useTranslation()
  const location = useLocation()

  const [agreeTerms] = useAgreeUserPolicyAgreementTermMutation()
  const [agreePrivacy] = useAgreeUserPolicyAgreementPrivacyMutation()
  const [agreeEmpl] = useAgreeUserPolicyNewEmplMutation()

  const onAgree = async () => {
    if (terms) await agreeTerms({})
    if (privacy) await agreePrivacy({})
    else if (empl) await agreeEmpl({})

    close()
  }

  const close = () => {
    window.location.href = returnPath ?? location.pathname
  }

  const content = useMemo(() => {
    if (terms && privacy) {
      return (
        <>
          <div className="UserAgreementModal__title">
            {t('agreement_popup.terms_and_policy_update.title')}
          </div>
          <div className="UserAgreementModal__description">
            <div className="UserAgreementModal__description__links">
              <Trans i18nKey="agreement_popup.terms_and_policy_update.description">
                <ExternalLink href={EXTERNAL_PATH.TERM()} />
                <ExternalLink href={EXTERNAL_PATH.POLICY()} />
                <img src="/images/user-agreement.svg" />
              </Trans>
            </div>
          </div>
        </>
      )
    }

    if (terms) {
      return (
        <>
          <div className="UserAgreementModal__title">
            {t('agreement_popup.terms_update.title')}
          </div>
          <div className="UserAgreementModal__description">
            <div className="UserAgreementModal__description__links">
              <Trans i18nKey="agreement_popup.terms_update.description">
                <ExternalLink href={EXTERNAL_PATH.TERM()} />
                <img src="/images/user-agreement.svg" />
              </Trans>
            </div>
          </div>
        </>
      )
    }

    if (privacy) {
      return (
        <>
          <div className="UserAgreementModal__title">
            {t('agreement_popup.policy_update.title')}
          </div>
          <div className="UserAgreementModal__description">
            <div className="UserAgreementModal__description__links">
              <Trans i18nKey="agreement_popup.policy_update.description">
                <ExternalLink href={EXTERNAL_PATH.POLICY()} />
                <img src="/images/user-agreement.svg" />
              </Trans>
            </div>
          </div>
        </>
      )
    }

    if (empl) {
      return (
        <>
          <div className="UserAgreementModal__title UserAgreementModal__title--warning">
            {t('agreement_popup.empl_update.title')}
          </div>
          <div className="UserAgreementModal__description">
            <div className="UserAgreementModal__description__image">
              <img
                src={`/images/icon-empl-coin.svg`}
                alt={t('agreement_popup.empl_update.title')}
              />
            </div>
            <div className="UserAgreementModal__subtitle">
              {t('agreement_popup.empl_update.subtitle')}
            </div>
            <div className="UserAgreementModal__box">
              <span
                dangerouslySetInnerHTML={{
                  __html: t('agreement_popup.empl_update.description_1'),
                }}
              />
            </div>
            <div className="UserAgreementModal__box">
              <span
                dangerouslySetInnerHTML={{
                  __html: t('agreement_popup.empl_update.description_2'),
                }}
              />
            </div>
            <div className="UserAgreementModal__description__links">
              <ExternalLink href={EXTERNAL_PATH.EMPL_INFO()}>
                {t('agreement_popup.empl_update.more_details')}
              </ExternalLink>
            </div>
          </div>
        </>
      )
    }

    return null
  }, [terms, privacy, empl])

  if (!content) {
    close()
  }

  return (
    <div className="UserAgreementModal Modal__wrapper Modal__wrapper--auto-h Modal__wrapper--auto-w">
      <div className="Modal__body">{content}</div>
      <div className="Modal__footer Modal__footer--center">
        <Button onClick={onAgree} fluid>
          {!!empl ? t('common.accept') : t('common.agree')}
        </Button>
      </div>
    </div>
  )
}
